import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CompanyTimeline = () => {
  const [activeYear, setActiveYear] = useState(2024);

  const timelineData = [
    {
      year: 2025,
      title: "Launch Day - Ready to Deliver",
      description: "BytePairs launches today with a mission to make enterprise-quality development accessible to every business.",
      icon: "Rocket",
      achievements: [
        "Cutting-edge technology stack ready",
        "Expert team assembled and prepared",
        "Modern development processes established",
        "Quality-first approach from day one"
      ],
      stats: {
        readiness: "100%",
        techStack: "15+",
        commitment: "Excellence"
      }
    },
    {
      year: "Vision",
      title: "Our Commitment",
      description: "We're committed to delivering premium quality at startup-friendly prices, making no compromises on excellence.",
      icon: "Target",
      achievements: [
        "Enterprise-grade code quality",
        "Transparent pricing and communication",
        "Latest technologies and best practices",
        "Long-term partnership focus"
      ],
      stats: {
        quality: "Premium",
        pricing: "Fair",
        support: "24/7"
      }
    },
    {
      year: "Future",
      title: "Growing Together",
      description: "As we grow with our clients, we'll maintain our core values of excellence, innovation, and partnership.",
      icon: "TrendingUp",
      achievements: [
        "Scalable solutions that grow with you",
        "Continuous learning and improvement",
        "Building lasting client relationships",
        "Innovation-driven development"
      ],
      stats: {
        scalability: "∞",
        innovation: "Always",
        partnership: "Forever"
      }
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Rocket" size={16} />
            <span>Launch Day 2025</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Ready to Build Your Vision
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            BytePairs launches today with a clear mission: deliver enterprise-quality development at startup-friendly prices. We're ready, capable, and committed to your success.
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-accent to-success rounded-full hidden lg:block"></div>

          <div className="space-y-12 lg:space-y-24">
            {timelineData.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex flex-col lg:flex-row items-center ${index % 2 === 0 ? 'lg:flex-row-reverse' : ''
                  }`}
                onMouseEnter={() => setActiveYear(item.year)}
              >
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shadow-brand-lg z-10 hidden lg:flex">
                  <Icon name={item.icon} size={24} color="white" />
                </div>

                {/* Content Card */}
                <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:pr-16' : 'lg:pl-16'}`}>
                  <div className={`bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-brand-lg transition-all duration-300 ${activeYear === item.year ? 'ring-2 ring-primary/20 shadow-brand-lg' : ''
                    }`}>
                    <div className="flex items-center space-x-4 mb-6 lg:hidden">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                        <Icon name={item.icon} size={20} color="white" />
                      </div>
                      <div className="text-2xl font-bold text-primary">{item.year}</div>
                    </div>

                    <div className="hidden lg:block text-2xl font-bold text-primary mb-4">{item.year}</div>
                    <h3 className="text-2xl font-bold text-foreground mb-4">{item.title}</h3>
                    <p className="text-text-secondary mb-6 leading-relaxed">{item.description}</p>

                    <div className="space-y-3 mb-6">
                      {item.achievements.map((achievement, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          <span className="text-sm text-text-secondary">{achievement}</span>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary">{Object.values(item.stats)[0]}</div>
                        <div className="text-xs text-text-secondary">{Object.keys(item.stats)[0]}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-accent">{Object.values(item.stats)[1]}</div>
                        <div className="text-xs text-text-secondary">{Object.keys(item.stats)[1]}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-success">{Object.values(item.stats)[2]}</div>
                        <div className="text-xs text-text-secondary">{Object.keys(item.stats)[2]}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;